import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormHelperText,
  Alert,
  Snackbar
} from "@mui/material";
import { useState } from "react";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useCreateNotificationCampaign } from '../../../clients/graphql/pages/notification-queries';

export type interactionType = 'clicked-on' | 'initiated' | 'subscribed'
export type portfolioProp = {
    porfolioProp : number,
    interactionTypes : interactionType[]
}

export enum FollowingType {
    PORTFOLIO = 'PORTFOLIO',
    TEAM = 'TEAM'
}

export enum DeliveryMethod {
    PUSH = 'PUSH',
    EMAIL = 'EMAIL'
}

export type teamProp = {
    teamProp : number,
    interactionTypes : interactionType[]
}

interface CampaignFormData {
  title: string;
  body: string;
  type: string;
  deliveryMethod: string;
  targetDefinition: {
    followingKey: string;
    followingType: string;
    interactionTypes: interactionType[];
  };
  startDate: Date | null;
  endDate: Date | null;
}

export const CreateCampaignsPage: React.FC = () => {
  const [formData, setFormData] = useState<CampaignFormData>({
    title: '',
    body: '',
    type: 'CUSTOM',
    deliveryMethod: '',
    targetDefinition: {
        followingKey: '',
        followingType: '',
        interactionTypes: []
    },
    startDate: null,
    endDate: null
  });

  const [startDateEnabled, setStartDateEnabled] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // GraphQL mutation hook
  const [createCampaign, { loading: isCreating }] = useCreateNotificationCampaign({
    onCompleted: (data: any) => {
      if (data?.createNotificationCampaign?.success) {
        setShowSuccessMessage(true);
        // Reset form after successful creation
        setFormData({
          type: 'CUSTOM',
          title: '',
          body: '',
          deliveryMethod: '',
          targetDefinition: {
            followingKey: '',
            followingType: '',
            interactionTypes: []
          },
          startDate: null,
          endDate: null
        });
        setStartDateEnabled(false);
        setHasSubmitted(false);
      } else {
        setErrorMessage('Failed to create campaign');
        setShowErrorMessage(true);
      }
    },
    onError: (error: any) => {
      setErrorMessage(error.message || 'An error occurred while creating the campaign');
      setShowErrorMessage(true);
    }
  });

  const handleInputChange = (field: keyof CampaignFormData | 'targetDefinition.followingKey' | 'targetDefinition.followingType', value: string) => {
    if (field.startsWith('targetDefinition.')) {
      const subField = field.split('.')[1] as keyof typeof formData.targetDefinition;
      setFormData(prev => ({
        ...prev,
        targetDefinition: {
          ...prev.targetDefinition,
          [subField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: Date | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleInteractionTypesChange = (event: SelectChangeEvent<interactionType[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      targetDefinition: {
        ...prev.targetDefinition,
        interactionTypes: typeof value === 'string' ? [] : value
      }
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);
    
    // Check if form is valid - both dates are now optional
    // No validation needed for dates
    
    try {
      // Transform form data to match the GraphQL input format
      const campaignInput = {
        type: formData.type, // This will be "CUSTOM"
        subject: formData.title,
        body: formData.body,
        deliveryMethod: formData.deliveryMethod,
        targetMode: "RULE",
        targetDefinition: formData.targetDefinition,
        scheduledAt: formData.startDate ? formData.startDate.toISOString() : null,
        endAt: formData.endDate ? formData.endDate.toISOString() : null
      };

      await createCampaign({
        variables: {
          input: campaignInput
        }
      });
      
    } catch (error) {
      console.error('Error creating campaign:', error);
      setErrorMessage('Failed to create campaign');
      setShowErrorMessage(true);
    }
  };

  const handleStartDateToggle = () => {
    setStartDateEnabled(!startDateEnabled);
    if (!startDateEnabled) {
      // When enabling, set to current time
      setFormData(prev => ({
        ...prev,
        startDate: new Date()
      }));
    } else {
      // When disabling, clear the start date
      setFormData(prev => ({
        ...prev,
        startDate: null
      }));
    }
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>Create Custom Campaign</Typography>
        
        <Grid container spacing={3}>
          {/* Left Menu */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Target Definition</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Following Type</InputLabel>
                <Select
                  value={formData.targetDefinition.followingType}
                  label="Following Type"
                  onChange={(e) => handleInputChange('targetDefinition.followingType', e.target.value)}
                >
                  {Object.values(FollowingType).map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Following Key"
                value={formData.targetDefinition.followingKey}
                onChange={(e) => handleInputChange('targetDefinition.followingKey', e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Enter following key..."
              />

              <FormControl fullWidth>
                <InputLabel>Interaction Types</InputLabel>
                <Select
                  multiple
                  value={formData.targetDefinition.interactionTypes}
                  onChange={handleInteractionTypesChange}
                  input={<OutlinedInput label="Interaction Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="clicked-on">Clicked On</MenuItem>
                  <MenuItem value="initiated">Initiated</MenuItem>
                  <MenuItem value="subscribed">Subscribed</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Delivery Method</InputLabel>
                <Select
                  value={formData.deliveryMethod}
                  label="Delivery Method"
                  onChange={(e) => handleInputChange('deliveryMethod', e.target.value)}
                >
                  {Object.values(DeliveryMethod).map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>

          {/* Right Form */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Campaign Message</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Campaign Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="Enter campaign title..."
                  required
                />

                <TextField
                  fullWidth
                  label="Campaign Body"
                  value={formData.body}
                  onChange={(e) => handleInputChange('body', e.target.value)}
                  multiline
                  rows={6}
                  sx={{ mb: 3 }}
                  placeholder="Enter campaign message..."
                  required
                />

                {/* Date/Time Fields */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                    Campaign Schedule
                  </Typography>
                  
                  {/* UTC Notice */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    All dates and times are in UTC timezone
                  </Alert>
                  
                  {/* Start Date Field */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 2 }}>
                        Start Date (Optional)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleStartDateToggle}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {startDateEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </Box>
                    <DateTimePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(value) => handleDateChange('startDate', value)}
                      disabled={!startDateEnabled}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiInputBase-input': {
                              color: !startDateEnabled ? 'text.disabled' : 'text.primary',
                            },
                            '& .MuiInputLabel-root': {
                              color: !startDateEnabled ? 'text.disabled' : 'text.primary',
                            },
                          }
                        }
                      }}
                    />
                    {!startDateEnabled && (
                      <FormHelperText sx={{ color: 'text.disabled' }}>
                        Campaign will start immediately when created
                      </FormHelperText>
                    )}
                  </Box>

                  {/* End Date Field */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 2 }}>
                        End Date (Optional)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (formData.endDate) {
                            handleDateChange('endDate', null);
                          } else {
                            handleDateChange('endDate', new Date());
                          }
                        }}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {formData.endDate ? 'Disable' : 'Enable'}
                      </Button>
                    </Box>
                    <DateTimePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={(value) => handleDateChange('endDate', value)}
                      disabled={!formData.endDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiInputBase-input': {
                              color: !formData.endDate ? 'text.disabled' : 'text.primary',
                            },
                            '& .MuiInputLabel-root': {
                              color: !formData.endDate ? 'text.disabled' : 'text.primary',
                            },
                          }
                        }
                      }}
                    />
                    {!formData.endDate && (
                      <FormHelperText sx={{ color: 'text.disabled' }}>
                        Campaign will run indefinitely until manually stopped
                      </FormHelperText>
                    )}
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating Campaign...' : 'Create Campaign'}
                </Button>
              </form>
            </Paper>
          </Grid>
        </Grid>

        {/* Success Message */}
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={6000}
          onClose={handleCloseSuccessMessage}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSuccessMessage} severity="success">
            Campaign created successfully!
          </Alert>
        </Snackbar>

        {/* Error Message */}
        <Snackbar
          open={showErrorMessage}
          autoHideDuration={6000}
          onClose={handleCloseErrorMessage}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseErrorMessage} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};